import type {
  User,
  UserPreferences,
  WorkspaceUser,
  AddWorkspaceUserVariables,
  UpdateWorkspaceUserRoleVariables,
  RemoveWorkspaceUserVariables,
} from "@/types/user";
import type { MeResponse } from "@/hooks/api/user/useGetMe";
import type {
  PreferenceSchema,
  PreferenceSchemaRow,
  ToggleableChannel,
  UpdateUserPreferencesInput,
  UserNotificationSettingsValue,
} from "@/types/notification";

import { useMockStore } from "../store";

import { inviteUserMock } from "./organization";

import { mergePreferenceValues } from "@/features/notifications/helpers/effective-preferences";
import { ORG_ROLE, WORKSPACE_ROLE } from "@/types/user";

/**
 * Get all users
 */
export function getUsersMock(): User[] {
  const store = useMockStore.getState();

  return store.users;
}

/**
 * Get all workspace users (users projected onto the WorkspaceUser shape).
 *
 * The roles are a deterministic mix rather than all-members so role-scoped UI
 * (the contacts Agents filter, role chips) has something to render.
 */
export function getWorkspaceUsersMock(workspaceId?: string): WorkspaceUser[] {
  const store = useMockStore.getState();
  const id = workspaceId ?? store.workspaces[0]?.id;

  if (id) {
    return store.users.flatMap((user) => {
      const assignment = user.workspaces.find(
        (workspace) => workspace.id === id,
      );

      if (!assignment && user.role !== ORG_ROLE.ADMIN) return [];

      return [
        {
          ...user,
          role:
            user.role === ORG_ROLE.ADMIN
              ? WORKSPACE_ROLE.ADMIN
              : assignment!.role,
          roleId: user.role === ORG_ROLE.ADMIN ? undefined : assignment?.roleId,
          organizationRole: user.role,
        },
      ];
    });
  }

  return store.users.map((user, index) => ({
    ...user,
    role:
      index === 0
        ? WORKSPACE_ROLE.ADMIN
        : index % 3 === 0
          ? WORKSPACE_ROLE.MEMBER
          : WORKSPACE_ROLE.AGENT,
    organizationRole: user.role,
  }));
}

export function addWorkspaceUserMock(
  input: AddWorkspaceUserVariables,
): WorkspaceUser {
  const store = useMockStore.getState();
  const workspace = store.workspaces.find(
    (item) => item.id === input.workspaceId,
  );

  if (!workspace) throw new Error("Workspace not found");
  const existing = store.users.find(
    (user) => user.email.toLowerCase() === input.email.toLowerCase(),
  );

  if (
    existing?.role === ORG_ROLE.ADMIN ||
    existing?.workspaces.some((item) => item.id === input.workspaceId)
  ) {
    throw new Error("This user already has access to this workspace");
  }
  const assignment = {
    id: workspace.id,
    name: workspace.name,
    role: input.role,
    roleId: input.roleId,
  };
  const user =
    existing ??
    inviteUserMock({
      ...input,
      role: ORG_ROLE.MEMBER,
      workspaces: [assignment],
    });

  if (existing)
    store.updateUser(existing.id, {
      workspaces: [...existing.workspaces, assignment],
    });

  return getWorkspaceUsersMock(workspace.id).find(
    (item) => item.id === user.id,
  )!;
}

export function updateWorkspaceUserRoleMock(
  input: UpdateWorkspaceUserRoleVariables,
): WorkspaceUser {
  const store = useMockStore.getState();
  const user = store.users.find((item) => item.id === input.userId);

  if (!user?.workspaces.some((item) => item.id === input.workspaceId))
    throw new Error("Workspace member not found");
  store.updateUser(user.id, {
    workspaces: user.workspaces.map((workspace) =>
      workspace.id === input.workspaceId
        ? { ...workspace, role: input.role, roleId: input.roleId }
        : workspace,
    ),
    updatedAt: new Date(),
  });

  return getWorkspaceUsersMock(input.workspaceId).find(
    (item) => item.id === user.id,
  )!;
}

export function removeWorkspaceUserMock(input: RemoveWorkspaceUserVariables): {
  success: boolean;
} {
  const store = useMockStore.getState();
  const user = store.users.find((item) => item.id === input.userId);

  if (!user) throw new Error("User not found");
  if (user.role === ORG_ROLE.ADMIN)
    throw new Error("Manage organization admins from organization settings");
  store.updateUser(user.id, {
    workspaces: user.workspaces.filter((item) => item.id !== input.workspaceId),
  });

  return { success: true };
}

/**
 * Get the current user
 */
export function getCurrentUserMock(): User | undefined {
  const store = useMockStore.getState();

  return store.users.find((u) => u.id === store.currentUserId);
}

/**
 * GET users/me - the current user with the presence summary the backend embeds.
 */
export function getMeMock(): MeResponse {
  const user = getCurrentUserMock();

  if (!user) {
    throw new Error("No current user found");
  }

  return {
    ...user,
    presence: {
      status: "online",
      lastSeenAt: new Date().toISOString(),
    },
  };
}

/**
 * Get a single user by ID
 */
export function getUserMock(userId: string): User | undefined {
  const store = useMockStore.getState();

  return store.users.find((u) => u.id === userId);
}

/**
 * Update a user
 */
export function updateUserMock(
  userId: string,
  updates: Partial<User>,
): User | undefined {
  const store = useMockStore.getState();
  const user = store.users.find((u) => u.id === userId);

  if (!user) return undefined;

  store.updateUser(userId, updates);

  return { ...user, ...updates };
}

/**
 * Save/create a new user
 */
export function saveUserMock(): User {
  const store = useMockStore.getState();
  const currentUser = store.users.find((u) => u.id === store.currentUserId);

  if (!currentUser) {
    throw new Error("No current user found");
  }

  return currentUser;
}

const ALL_CHANNELS: ToggleableChannel[] = ["mobile_push", "web_push", "email"];
const PUSH_CHANNELS: ToggleableChannel[] = ["mobile_push", "web_push"];
const EMAIL_ONLY: ToggleableChannel[] = ["email"];
const DEFAULT_CHANNEL_STATE = {
  mobile_push: true,
  web_push: true,
  email: false,
};

/**
 * The five collapsed conversation-activity types, worded once for both rows. The
 * scope leads and this list follows, so the reader knows which conversations are
 * meant before wading through what counts as activity on them.
 */
const ACTIVITY_EVENTS =
  "a new message, a form filled in, a follow-up coming due, a snooze ending, or the AI handing over to a human";

const scenarioRow = (
  row: Omit<PreferenceSchemaRow, "defaults">,
): PreferenceSchemaRow => ({
  ...row,
  defaults: Object.fromEntries(
    row.channels.map((c) => [c, DEFAULT_CHANNEL_STATE[c]]),
  ),
});

/**
 * Mirrors the backend scenario.registry.ts - labels, channels and row order.
 * The last three rows are admin-only there and simply absent from a
 * non-admin's schema; the mock always serves the admin view, which is also why
 * the weekly digest type is present.
 */
const MOCK_PREFERENCE_SCHEMA: PreferenceSchema = {
  soundDefault: true,
  soundKeyDefault: "chime",
  mobilePushWhenAwayDefault: true,
  sounds: [
    {
      key: "chime",
      label: "Chime",
      description: "Soft and warm",
      url: "https://assets.replyn.io/sounds/alert-1-v2.wav",
    },
    {
      key: "ping",
      label: "Ping",
      description: "Short and crisp",
      url: "https://assets.replyn.io/sounds/alert-2-v2.wav",
    },
    {
      key: "bell",
      label: "Bell",
      description: "Rings out, hard to miss",
      url: "https://assets.replyn.io/sounds/alert-3-v2.wav",
    },
  ],
  digest: {
    minuteStep: 30,
    types: [
      {
        key: "daily",
        label: "Daily digest",
        description: "One email each morning at the time you pick.",
        defaults: { hour: 8, minute: 0 },
      },
      {
        key: "weekly",
        label: "Weekly summary",
        description:
          "A team summary once a week, on the day and time you pick.",
        defaults: { hour: 9, minute: 0, dayOfWeek: 1 },
      },
    ],
  },
  scenarios: [
    scenarioRow({
      key: "my_conversations",
      label: "My conversations",
      description: `Activity on conversations assigned to you: ${ACTIVITY_EVENTS}.`,
      channels: PUSH_CHANNELS,
    }),
    {
      ...scenarioRow({
        key: "unassigned_conversations",
        label: "Unassigned conversations",
        description: `Activity on conversations nobody has picked up yet: ${ACTIVITY_EVENTS}.`,
        channels: PUSH_CHANNELS,
      }),
      // On by default for the agent role alone, and the mock serves the admin view.
      defaults: { mobile_push: false, web_push: false },
    },
    scenarioRow({
      key: "new_assignment",
      label: "New assignment",
      description: "Someone hands you a conversation",
      channels: PUSH_CHANNELS,
    }),
    scenarioRow({
      key: "note_mentions",
      label: "Mentions",
      description:
        "A teammate mentions you in a note, or the AI asks you a question",
      channels: PUSH_CHANNELS,
    }),
    scenarioRow({
      key: "appointment_updates",
      label: "Appointment updates",
      description:
        "An appointment changes, or one has come and gone without anyone saying how it went",
      channels: ALL_CHANNELS,
    }),
    {
      ...scenarioRow({
        key: "sla_breached",
        label: "SLA breached",
        description: "A conversation missed its response SLA",
        channels: ALL_CHANNELS,
      }),
      // Every breach on every conversation reaches every admin, so the registry
      // makes this row opt-in rather than on by default.
      defaults: { mobile_push: false, web_push: false, email: false },
    },
    scenarioRow({
      key: "broadcast_events",
      label: "Broadcast events",
      description:
        "A broadcast starts sending or finishes, and if one is paused, resumed, cancelled or fails",
      channels: ALL_CHANNELS,
    }),
    scenarioRow({
      key: "ai_activity",
      label: "AI activity",
      description:
        "A test run on one of your AI agents finishes, or the AI leaves a suggestion",
      channels: ALL_CHANNELS,
    }),
    scenarioRow({
      key: "storefront_orders",
      label: "Storefront orders",
      description:
        "Someone places an order on your storefront. Orders you create yourself from the inbox don't count.",
      channels: EMAIL_ONLY,
    }),
  ],
};

let mockPreferenceValues: UserNotificationSettingsValue = {};

/**
 * GET users/preferences
 */
export function getUserPreferencesMock(): UserPreferences {
  return { schema: MOCK_PREFERENCE_SCHEMA, values: mockPreferenceValues };
}

/**
 * PATCH users/preferences - deep-merges the sparse body like the server does.
 */
export function updateUserPreferencesMock(
  input: UpdateUserPreferencesInput,
): UserPreferences {
  mockPreferenceValues = mergePreferenceValues(mockPreferenceValues, input);

  return getUserPreferencesMock();
}
